import { useCallback, useContext, useEffect, useState } from "react";

import EcheckinDao from "../../dao/EcheckinDao";
import StudentInfoDao from "../../dao/StudentInfoDao";

import { DASH_LINKS } from "../../const/AppCardLinks";
import { formatDate } from "../DateFormatter";
import { GlobalContext } from "../../components/contextProvider/ContextProvider";
import { isWindowOpen } from "../ActionControlWindows";

/**
 * TRANSPORTATION BUSINESS LOGIC
 * ------------------------------------------------------------------------------------------------
 * Student must have a studentNumber
 * Student must NOT be a Home School Student -/student-info/student/{$personId}/{$schoolId}/detail.json
 * get StudentBusRoute -> /student-info/busRoute/{$studentNumber}/detail.json
 * studentBusRoute and transportationWindow is open and
 * transportationLocation -> echeckin/transportation_school/by_location/{locKey}/detail.json and
 * studentBusRoute.eligibleToRide is true
 * buttons are "Transportation” or “Manage Transportation Options”
 * ------------------------------------------------------------------------------------------------
 * Custom hook to determine if a student's guardian has access to the Transportation app tile
 * @name useBusApp
 * @param {*} allLocationDtos
 * @param {*} student
 * @param {*} token
 * @returns {{}} {appObj, loading}
 */
export const useBusApp = (isGuardian, student) => {
    const { dispatch, state } = useContext(GlobalContext);
    const { allLocationDtos, schoolYearDto, studentAppObjects, studentsHomeSchooled, token } = state || {};

    const [appObj, setAppObj] = useState(null);
    const [firstRun, setFirstRun] = useState(true);
    const [loading, setLoading] = useState(true);
    const [locationDto, setLocationDto] = useState(null);

    const studentKey = student?.key || null;
    const existing = studentAppObjects?.[studentKey]?.TRANSPORTATION || null;

    /**
     * @name dispatchAppObject
     * @description The obj passed in will be added to the student's available applications
     * @callback
     * @param {{}} obj
     */
    const dispatchAppObject = useCallback(
        (obj) => {
            dispatch({
                type: "StudentAppObjects",
                studentAppObjects: {
                    ...studentAppObjects,
                    [studentKey]: {
                        ...studentAppObjects?.[studentKey],
                        TRANSPORTATION: {
                            appObj: obj
                        }
                    }
                }
            });
        },
        [dispatch, studentAppObjects, studentKey]
    );

    /**
     * If we have the student's apps in context, set the appObj for return
     */
    useEffect(() => {
        if (existing) {
            setAppObj(existing.appObj);
            setLoading(false);
        }
    }, [existing]);

    /**
     * Get the selected student's school location
     */
    useEffect(() => {
        if (existing) {
            return;
        }
        if (!allLocationDtos || !student) {
            setAppObj(null);
            setLoading(false);

            return;
        }

        /**
         * @name getLocationDto
         * @description Retrieve the selected student's school location
         * @returns {{} | null}
         */
        const getLocationDto = () => {
            try {
                const { locationKey, schoolId } = student;
                let locMatch = [];
                if (schoolId) {
                    locMatch = allLocationDtos.reduce((result, obj) => {
                        const { icId } = obj;
                        const schoolMatch = icId.filter((item) => parseInt(item, 10) === parseInt(schoolId, 10));
                        if (schoolMatch && schoolMatch.length > 0) {
                            result.push(obj);
                        }

                        return result;
                    }, []);
                } else if (locationKey) {
                    locMatch = allLocationDtos.filter((obj) => parseInt(obj.key, 10) === parseInt(locationKey, 10));
                }
                if (locMatch && locMatch?.length > 0) {
                    setLocationDto(locMatch[0]);
                }
            } catch (error) {
                console.error(`Unable to retrieve a Location for Student ${student.studentNumber}:\n`, error);
            }
        };

        getLocationDto();
    }, [allLocationDtos, existing, student]);

    /**
     * Retrieve the transportation app object, if the student's bus route is valid and he is not homeschooled
     * Also, update the appObj depending on application status
     */
    useEffect(() => {
        if (existing) {
            return;
        }
        if (!isGuardian || !locationDto || !schoolYearDto || !token || !student?.studentNumber) {
            setAppObj(null);
            setLoading(false);

            return;
        }

        /**
         * @name fetchBusRoute
         * @description Retrieve the student's bus route
         * @async
         * @returns {{} | null}
         */
        const fetchBusRoute = async () => {
            try {
                const options = {
                    action: "studentBusRoutesRead",
                    studentNumber: student.studentNumber,
                    token
                };
                const response = await StudentInfoDao(options);
                if (response && response.data) {
                    const { payload } = response.data;
                    if (payload && payload.eligibleToRide) {
                        return payload;
                    }
                }

                return null;
            } catch (error) {
                console.error("Unable to retrieve Bus Routes:\n", error);
            }
        };

        /**
         * @name fetchCsa
         * @description Retrieve the student's Custom Student Attributes
         * @async
         * @returns {{} | null}
         */
        const fetchCsa = async () => {
            try {
                const options = {
                    action: "customAttributeRead",
                    attributeName: "TransportationOption",
                    objectName: "Enrollment",
                    params: {
                        currentEnrollment: true
                    },
                    studentNumber: student.studentNumber,
                    token
                };
                const response = await StudentInfoDao(options);
                if (response && response.data) {
                    const { payload } = response.data;

                    return payload;
                }

                return null;
            } catch (error) {
                console.error(`Unable to retrieve CSA for student ${student.studentNumber}:\n`, error);
            }
        };

        /**
         * @name fetchHasApplied
         * @description Retrieve the student's most recent Bus Pass Application
         * @async
         * @returns {{} | null}
         */
        const fetchHasApplied = async () => {
            try {
                const { studentNumber } = student;
                const options = {
                    action: "busPassApplicationsByStudentNumber",
                    schoolYearKey: schoolYearDto.key,
                    studentNumber,
                    token
                };
                const response = await EcheckinDao(options);
                if (response && response.data) {
                    const { payload } = response.data;
                    if (payload.length) {
                        payload.sort((a, b) => {
                            return parseInt(a.key, 10) > parseInt(b.key, 10) ? 1 : -1;
                        });

                        return payload[0];
                    } else {
                        // get Historical data
                        const hOptions = {
                            action: "histBusPassApplicationsByStudentNumber",
                            schoolYearKey: schoolYearDto.key,
                            studentNumber,
                            token
                        };
                        const hResponse = await EcheckinDao(hOptions);
                        if (hResponse && hResponse.data) {
                            const hPayload = hResponse.data.payload;
                            if (hPayload.length) {
                                hPayload.sort((a, b) => {
                                    return parseInt(a.key, 10) < parseInt(b.key, 10) ? 1 : -1;
                                });

                                return hPayload[0];
                            }
                        }
                    }
                }

                return null;
            } catch (error) {
                console.error("Unable to retrieve Bus Pass Applications:\n", error);
            }
        };

        /**
         * @name fetchIsHomeSchooled
         * @description Retrieve the student's Home Schooled Status
         * @async
         * @returns {bool}
         */
        const fetchIsHomeSchooled = () => {
            try {
                const { studentNumber } = student;
                const homeSchoolMatch = studentsHomeSchooled?.find(
                    (hsObj) => parseInt(hsObj.studentNumber, 10) === parseInt(studentNumber, 10)
                );

                return homeSchoolMatch?.isHomeSchooled;
            } catch (error) {
                console.error("Unable to find Home Schooled Status:\n", error);
            }
        };

        /**
         * @name fetchTransportationApp
         * @description Retrieve the student's Transportation Checkin Windows
         * @async
         * @returns {{transObj: {}, payload: {}}
         */
        const fetchTransportationApp = async () => {
            try {
                const options = {
                    action: "checkinWindowTransportation",
                    params: {
                        locationId: locationDto.key
                    },
                    schoolYearKey: schoolYearDto.key,
                    token
                };
                const response = await EcheckinDao(options);
                if (response && response.data) {
                    const { payload } = response.data;
                    if (payload && isWindowOpen(payload?.checkinStartDate, payload?.checkinEndDate)) {
                        const transObj = {
                            app: "TRANSPORTATION",
                            body: isWindowOpen(payload.checkinStartDate, payload.annualPassOfferEndDate)
                                ? "BusApp_Body1" // Apply for a bus pass and/or elect annual/quarterly payment options for bus rides.
                                : "BusApp_Body2", // Apply for a buss pass.
                            buttons: [
                                {
                                    label: "BusApp_Btn1",
                                    href: `${DASH_LINKS.transportation}/${student.studentNumber}`
                                }
                            ],
                            endDate: formatDate(payload.checkinEndDate, true, true),
                            icon: "TRANSPORTATION_ICON",
                            moreInfo: null,
                            startDate: formatDate(payload.checkinStartDate, true, true),
                            status: null,
                            statusColor: "#0669B3", // pearl-blue
                            subBody: null,
                            subStatus: isWindowOpen(payload.checkinStartDate, payload.annualPassOfferEndDate)
                                ? "BusApp_AnnualOffer"
                                : null,
                            subStatusRange: isWindowOpen(payload.checkinStartDate, payload.annualPassOfferEndDate)
                                ? formatDate(payload.annualPassOfferEndDate, true, true)
                                : null,
                            title: "BusApp_Header"
                        };

                        return { transObj, payload };
                    }
                }

                return { transObj: null, payload: null };
            } catch (error) {
                console.error("Unable to retrieve Transportation Checkin Windows:\n", error);
            }
        };

        /**
         * @name getBusPassStatus
         * @description Update the appObj depending on application status
         * @param {{}} app
         * @param {{}} csa
         * @param {bool} hasApplied
         * @param {{}} window
         * @returns {{}}
         */
        const getBusPassStatus = (app, csa, hasApplied, window) => {
            try {
                let updatedApp = app;
                if (hasApplied && hasApplied?.cardStatus === "PROCESSED") {
                    updatedApp = {
                        ...app,
                        // Bus Pass Issued
                        status: "BusApp_BusPass",
                        statusColor: "#4a8319", // green
                        // (
                        //     <>
                        //         Your bus pass has been issued. You may now use the bus.
                        //         <br />
                        //         If a card is damaged or lost, a replacement bus pass can be requested through{" "}
                        //         <a
                        //             className="anchor"
                        //             href="https://www.k12insight.com/Lets-Talk/DialogueCustom.aspx?k=WK3Z6DLT@DG2T5DLT"
                        //             rel="noreferrer"
                        //             target="_blank"
                        //         >
                        //             Let&apos;s Talk
                        //         </a>
                        //         .
                        //     </>
                        // )
                        subBody: "BusApp_BusPassIssued"
                    };
                } else if (hasApplied && hasApplied?.cardStatus === "UNPROCESSED") {
                    updatedApp = {
                        ...app,
                        // Bus Pass Processing
                        status: "BusApp_Processing",
                        statusColor: "#0669B3", // pearl-blue
                        // Your Bus Pass application is still processing.
                        subBody: "BusApp_BusPassProcessing"
                    };
                } else if (csa && csa?.value === "N") {
                    updatedApp = {
                        ...app,
                        // Not Riding
                        status: "BusApp_NotRiding",
                        statusColor: "#CB321A", // red
                        subBody: "BusApp_NotRidingReason3",
                        subBodyRange: `(${student.firstName})`
                    };
                } else {
                    updatedApp = {
                        ...app,
                        status: "BusApp_NoBusPass",
                        statusColor: "#707070", // grey3a
                        subBody: null
                    };
                }
                if (csa && csa?.value === "YQ") {
                    if (isWindowOpen(window.checkinStartDate, window.annualPassOfferEndDate)) {
                        updatedApp = {
                            ...updatedApp,
                            // You have elected Quarterly Billing. You can opt into Annual Billing by
                            subBody2: "BusApp_Quarterly",
                            subBody2Range: `(${formatDate(window.annualPassOfferEndDate, true, true)})`
                        };
                    }
                }

                return updatedApp;
            } catch (error) {
                console.error("Unable to retrieve Bus Pass Status:\n", error);
            }
        };

        const run = async () => {
            try {
                setLoading(true);
                const isHomeSchooled = fetchIsHomeSchooled();
                if (!isHomeSchooled) {
                    const busRoute = await fetchBusRoute();
                    if (busRoute?.eligibleToRide) {
                        const { transObj: app, payload: window } = await fetchTransportationApp();
                        if (app) {
                            const csa = await fetchCsa();
                            const hasApplied = await fetchHasApplied();
                            const updatedApp = getBusPassStatus(app, csa, hasApplied, window);
                            dispatchAppObject(updatedApp);

                            return;
                        }
                    }
                }
                dispatchAppObject(null);

                return;
            } catch (error) {
                console.error("Unable to retrieve Transportation App:\n", error);
            } finally {
                setLoading(false);
                setFirstRun(true);
            }
        };

        if (firstRun) {
            setFirstRun(false);
            run();
        }
    }, [
        allLocationDtos,
        dispatchAppObject,
        existing,
        firstRun,
        isGuardian,
        locationDto,
        schoolYearDto,
        student,
        studentsHomeSchooled,
        token
    ]);

    return { appObj, loading };
};
